import { ReactNode, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useStore } from "../store";

interface Props {
  children: ReactNode;
}

/**
 * Wraps protected routes. On mount, attempts to bootstrap the auth state
 * from a stored token. While that's pending, renders a tiny splash. If
 * authentication fails or there's no token, redirects to /login (preserving
 * the original location so the user lands back on it after sign-in).
 */
export default function AuthGate({ children }: Props) {
  const currentUser = useStore((s) => s.currentUser);
  const authChecked = useStore((s) => s.authChecked);
  const bootstrapAuth = useStore((s) => s.bootstrapAuth);
  const location = useLocation();

  useEffect(() => {
    if (!authChecked) {
      bootstrapAuth();
    }
  }, [authChecked, bootstrapAuth]);

  if (!authChecked) {
    return (
      <div className="auth-splash">
        <div className="auth-splash-spinner" aria-hidden="true" />
        <p>Loading…</p>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
